/**
 * API Client for SlideFactory backend
 *
 * Handles authentication, token refresh, and provides
 * type-safe API methods.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

interface RequestOptions extends Omit<RequestInit, 'headers'> {
  params?: Record<string, string | number | boolean>
  headers?: Record<string, string>
}

interface ApiResponse<T = unknown> {
  data: T
  status: number
  headers: Headers
}

class ApiClient {
  private accessToken: string | null = null
  private onSessionExpiredCallback: (() => void) | null = null

  constructor() {
    // Load token from localStorage on init
    this.accessToken = localStorage.getItem('accessToken')
  }

  setAccessToken(token: string | null): void {
    this.accessToken = token
  }

  onSessionExpired(callback: () => void): void {
    this.onSessionExpiredCallback = callback
  }

  private buildUrl(path: string, params?: Record<string, string | number | boolean>): string {
    const url = new URL(path, window.location.origin)

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value))
        }
      })
    }

    return url.toString()
  }

  private async request<T>(
    method: string,
    path: string,
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    const { params, body, headers: customHeaders, ...restOptions } = options

    const url = this.buildUrl(`${API_BASE_URL}${path}`, params)

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...customHeaders
    }

    // Add authorization header if we have a token
    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`
    }

    const response = await fetch(url, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'include', // Include cookies for refresh token
      ...restOptions
    })

    // Handle 401 - try token refresh
    if (response.status === 401 && path !== '/api/auth/refresh' && path !== '/api/auth/login') {
      const refreshed = await this.refreshToken()
      if (refreshed) {
        // Retry the original request
        headers['Authorization'] = `Bearer ${this.accessToken}`
        const retryResponse = await fetch(url, {
          method,
          headers,
          body: body ? JSON.stringify(body) : undefined,
          credentials: 'include',
          ...restOptions
        })
        return this.handleResponse<T>(retryResponse)
      }
    }

    return this.handleResponse<T>(response)
  }

  private async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
    let data: T

    const contentType = response.headers.get('content-type')
    if (contentType?.includes('application/json')) {
      data = await response.json()
    } else {
      data = await response.text() as unknown as T
    }

    if (!response.ok) {
      const error = new ApiError(
        (data as any)?.detail || (data as any)?.message || 'Request failed',
        response.status,
        data
      )
      throw error
    }

    return {
      data,
      status: response.status,
      headers: response.headers
    }
  }

  private async refreshToken(): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      })

      if (response.ok) {
        const data = await response.json()
        this.accessToken = data.access_token
        localStorage.setItem('accessToken', data.access_token)
        return true
      }
    } catch {
      // Refresh failed
    }

    // Clear invalid token
    this.accessToken = null
    localStorage.removeItem('accessToken')

    // Notify about session expiration
    if (this.onSessionExpiredCallback) {
      this.onSessionExpiredCallback()
    }

    return false
  }

  /**
   * Ensure the access token is valid by calling /api/auth/validate.
   * If the token is expired, the 401 interceptor will refresh it automatically.
   * Returns true if a valid token is available, false if session is fully expired.
   */
  async ensureValidToken(): Promise<boolean> {
    try {
      await this.request('GET', '/api/auth/validate')
      return true
    } catch {
      return false
    }
  }

  // HTTP methods
  async get<T>(path: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>('GET', path, options)
  }

  async post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>('POST', path, { ...options, body: body as any })
  }

  async put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', path, { ...options, body: body as any })
  }

  async patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>('PATCH', path, { ...options, body: body as any })
  }

  async delete<T>(path: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', path, options)
  }
}

export class ApiError extends Error {
  status: number
  data: unknown

  constructor(message: string, status: number, data?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

// Export singleton instance
export const api = new ApiClient()

// Export types
export type { ApiResponse, RequestOptions }
